import { defineStore } from 'pinia'
import { useAuthStore } from './auth'
import { photoFramingAPI, type CompletedPhotoFraming, type PhotoFramingScope } from '@/api/photoFraming'
import { flushPhotoFrames, pendingPhotoFrame, photoQueueStorage, type PendingPhotoFrame, type PhotoQueueStorage } from '@/features/session-capture/face-framing/persistence'

export const usePhotoFramingStore = defineStore('photoFraming', () => {
  const auth = useAuthStore()
  // Keep retrying in this tab if browser storage is temporarily unavailable.
  const memory = new Map<string, PendingPhotoFrame>()
  const storage: PhotoQueueStorage = {
    async put(row) { memory.set(row.id, row); try { await photoQueueStorage.put(row) } catch { /* keep in memory */ } },
    async all() {
      const merged = new Map<string, PendingPhotoFrame>()
      try { for (const row of await photoQueueStorage.all()) merged.set(row.id, row) } catch { /* memory fallback */ }
      for (const [id, row] of memory) merged.set(id, row)
      return [...merged.values()]
    },
    async remove(id) { try { await photoQueueStorage.remove(id) } catch { /* server writes are idempotent */ } memory.delete(id) },
  }
  let active: Promise<void> | undefined
  let timer: ReturnType<typeof setInterval> | undefined
  let running = false
  const sync = () => {
    if (active) return active
    active = flushPhotoFrames({ storage,
      identity: () => auth.user?.id && auth.token ? { actorId: auth.user.id, token: auth.token } : null,
      online: () => navigator.onLine,
      send: (row, token) => photoFramingAPI.save(row.scope, row.framing, token),
    }).catch(() => { /* derived metadata remains queued; scoring is independent */ }).finally(() => { active = undefined })
    return active
  }
  async function enqueue(scope: PhotoFramingScope, framing: CompletedPhotoFraming) {
    if (!auth.user?.id || !auth.token) return
    await storage.put(pendingPhotoFrame(auth.user.id, scope, framing))
    void sync()
  }
  const wake = () => { void sync() }
  function start() {
    if (running) return
    running = true
    window.addEventListener('online', wake)
    window.addEventListener('focus', wake)
    timer = setInterval(wake, 30000)
    wake()
  }
  function stop() { running = false; clearInterval(timer); window.removeEventListener('online', wake); window.removeEventListener('focus', wake) }
  return { enqueue, sync, start, stop }
})
